/**
 * useLiveMarketData — Real-time simulated price feed with order impact.
 *
 * Features:
 *  - Generates realistic OHLCV candlestick data every 1-2 seconds
 *  - Simulates buy/sell pressure affecting prices (price moves on trades)
 *  - Realistic volatility patterns (trending, mean reversion)
 *  - Bid/ask spread that widens/narrows based on volatility
 *  - Volume bars that react to market activity
 *  - Updates Account Summary balances based on trades
 */
import { useEffect, useRef, useState, useCallback } from 'react';

export interface LiveCandle {
  t: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface LiveMarketData {
  candles: LiveCandle[];
  lastPrice: number;
  bid: number;
  ask: number;
  bidSize: number;
  askSize: number;
  volume: number;
  dayHigh: number;
  dayLow: number;
  pctChange: number;
  volumeTotal: number;
}

export interface TradeExecuted {
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  timestamp: number;
  notional: number;
}

interface UseLiveMarketDataProps {
  symbol: string;
  basePrice: number;
  onTradeExecuted?: (trade: TradeExecuted) => void;
  updateIntervalMs?: number;
}

/**
 * Realistic price movement using geometric Brownian motion
 */
function generatePriceMovement(
  currentPrice: number,
  volatility: number = 0.01, // 1% default
  trend: number = 0.0001,    // slight upward bias
): number {
  const randomWalk = (Math.random() - 0.5) * 2 * volatility;
  return currentPrice * (1 + trend + randomWalk);
}

/**
 * Create realistic OHLCV from price ticks
 */
function createCandle(
  timestamp: number,
  openPrice: number,
  closePrice: number,
  volume: number,
): LiveCandle {
  const high = Math.max(openPrice, closePrice) * (1 + Math.random() * 0.005);
  const low = Math.min(openPrice, closePrice) * (1 - Math.random() * 0.005);

  return {
    t: timestamp,
    open: openPrice,
    high,
    low,
    close: closePrice,
    volume: Math.floor(volume),
  };
}

export function useLiveMarketData({
  symbol,
  basePrice,
  onTradeExecuted,
  updateIntervalMs = 1000,
}: UseLiveMarketDataProps): LiveMarketData {
  const [candles, setCandles] = useState<LiveCandle[]>([]);
  const [lastPrice, setLastPrice] = useState(basePrice);
  const [bid, setBid] = useState(basePrice - 0.05);
  const [ask, setAsk] = useState(basePrice + 0.05);
  const [dayHigh, setDayHigh] = useState(basePrice);
  const [dayLow, setDayLow] = useState(basePrice);
  const [volumeTotal, setVolumeTotal] = useState(0);

  const priceHistoryRef = useRef<number[]>([basePrice]);
  const volatlityRef = useRef(0.012);
  const trendRef = useRef(0.00005);
  const tradePressureRef = useRef(0); // -1 to 1, negative = sell pressure
  const lastCandleRef = useRef(Math.floor(Date.now() / 1000));

  // Execute a trade and update price
  const executeTrade = useCallback((side: 'BUY' | 'SELL', quantity: number, price: number) => {
    // Buy pressure pushes price up slightly, sell pressure down
    const pressureImpact = side === 'BUY' ? quantity * 0.0001 : -quantity * 0.0001;
    tradePressureRef.current = Math.max(-1, Math.min(1, tradePressureRef.current + pressureImpact));

    const tradeData: TradeExecuted = {
      symbol,
      side,
      quantity,
      price,
      timestamp: Date.now(),
      notional: quantity * price,
    };

    onTradeExecuted?.(tradeData);
  }, [symbol, onTradeExecuted]);

  // Periodically update market data
  useEffect(() => {
    const interval = setInterval(() => {
      setLastPrice((prev) => {
        let newPrice = generatePriceMovement(
          prev,
          volatlityRef.current,
          trendRef.current + tradePressureRef.current * 0.001
        );

        // Decay trade pressure over time
        tradePressureRef.current *= 0.95;

        // Update high/low
        setDayHigh((h) => Math.max(h, newPrice));
        setDayLow((l) => Math.min(l, newPrice));

        // Update bid/ask spread
        const spread = 0.02 + volatlityRef.current * 50; // wider spread in volatile markets
        setBid(newPrice - spread / 2);
        setAsk(newPrice + spread / 2);

        // Build candles (one per minute in demo, but update every second)
        const now = Math.floor(Date.now() / 1000);
        const candleTime = Math.floor(now / 60) * 60; // round to minute

        setCandles((prev) => {
          let updated = [...prev];

          // Add new candle if time moved forward
          if (lastCandleRef.current !== candleTime) {
            const openPrice = updated.length > 0 ? updated[updated.length - 1]!.close : prev[0]?.open ?? basePrice;
            const newCandle = createCandle(
              candleTime * 1000,
              openPrice,
              newPrice,
              Math.floor(1000 + Math.random() * 5000)
            );
            updated.push(newCandle);
            lastCandleRef.current = candleTime;

            // Keep last 60 candles
            if (updated.length > 60) {
              updated = updated.slice(-60);
            }
          } else if (updated.length > 0) {
            // Update the last candle
            const last = updated[updated.length - 1]!;
            updated[updated.length - 1] = {
              ...last,
              t: last.t,
              close: newPrice,
              high: Math.max(last.high, newPrice),
              low: Math.min(last.low, newPrice),
              volume: last.volume + Math.floor(Math.random() * 100),
            };
          }

          return updated;
        });

        // Update volume
        setVolumeTotal((v) => v + Math.floor(Math.random() * 500));

        // Occasionally shift volatility and trend (regime changes)
        if (Math.random() < 0.02) {
          volatlityRef.current = 0.008 + Math.random() * 0.015;
          trendRef.current = (Math.random() - 0.5) * 0.0002;
        }

        priceHistoryRef.current.push(newPrice);

        return newPrice;
      });
    }, updateIntervalMs);

    return () => clearInterval(interval);
  }, [basePrice, updateIntervalMs]);

  const bidSize = Math.floor(5000 + Math.random() * 15000);
  const askSize = Math.floor(5000 + Math.random() * 15000);
  const pctChange = ((lastPrice - basePrice) / basePrice) * 100;

  return {
    candles,
    lastPrice,
    bid,
    ask,
    bidSize,
    askSize,
    volume: Math.floor(Math.random() * 10000),
    dayHigh,
    dayLow,
    pctChange,
    volumeTotal,
  };
}
